/**
 * A single task, on its own screen.
 *
 * The prototype makes this a destination rather than a side panel, and its
 * subtitle says why: everything the four-page hunt used to require, in one
 * place. A panel beside the list cannot hold the timing breakdown, the
 * attributes and both payloads without becoming a scroll-within-a-scroll.
 *
 * The kicker carries the outcome — "failed after 2.3s" — so the state is read
 * before the id is.
 *
 * The URL's query string is the list this task was opened from: its filters,
 * tab, and page, carried verbatim by the row link. The back link returns to
 * that exact view, on the workspace route when the tenant is in the path,
 * and previous/next walk that list in the order it was shown.
 */
import { getTask, getTaskInput } from '@optum/api-client/endpoints/tasks';
import type { Task } from '@optum/api-client/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { qk } from '../../lib/query-keys.js';
import { useCurrentOrgId } from '../../lib/use-current-org.js';
import { Button } from '../../ui/Button.js';
import { RecordShape } from '../../ui/shapes.js';
import { Inline, Typography } from '../../ui/finixt.js';
import type { ReplaySeed } from '../sandbox/SandboxPage.js';
import { TaskDetailBody } from './TaskDetailBody.js';
import {
  hasListContext,
  useTaskNeighbours,
  type Neighbour,
  type TaskNeighbours,
} from './task-neighbours.js';
import { readTasksUrlState, withPage } from './tasks-url-state.js';

export function TaskDetailPage() {
  const { taskId = '' } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { search } = useLocation();
  const scopedOrgId = useCurrentOrgId();
  const listPath = scopedOrgId ? `/orgs/${scopedOrgId}/tasks` : '/tasks';
  const listState = useMemo(() => readTasksUrlState(new URLSearchParams(search)), [search]);
  const neighbours = useTaskNeighbours(taskId, listState, scopedOrgId, hasListContext(search));
  const task = useQuery({ queryKey: qk.task(taskId), queryFn: () => getTask(taskId) });
  // Fetched here so Replay can hand the real input over rather than opening an
  // empty sandbox and asking the operator to retype what already failed.
  const input = useQuery({
    queryKey: qk.taskInput(taskId),
    queryFn: () => getTaskInput(taskId, { inline: true }),
    enabled: Boolean(taskId),
  });

  // An input stored as a file comes back inline too; one that did not load
  // has nothing to seed the form with.
  const replayable = input.data?.inline !== null && input.data?.inline !== undefined;

  return (
    <RecordShape
      back={{ to: `${listPath}${search}`, label: 'Tasks' }}
      eyebrow={
        <Typography variant="eyebrow">{task.data ? kickerFor(task.data) : 'Task'}</Typography>
      }
      title={<span className="mono">{taskId}</span>}
      subtitle="What ran, where the time went, and what came back."
      meta={
        <NeighbourNav
          neighbours={neighbours}
          onStep={(n) =>
            navigate({ pathname: `${listPath}/${n.id}`, search: withPage(search, n.page) })
          }
        />
      }
      action={
        <Button
          variant="secondary"
          icon="science"
          disabled={!task.data || input.isPending}
          title={
            replayable
              ? 'Open the sandbox with this task’s capability and input'
              : 'The input did not come back to seed the form; open it from the Input section'
          }
          onClick={() =>
            task.data &&
            navigate('/sandbox', {
              state: {
                replay: {
                  taskId,
                  capabilityId: task.data.capability_id,
                  input: input.data?.inline ?? null,
                },
              } satisfies { replay: ReplaySeed },
            })
          }
        >
          Replay in sandbox
        </Button>
      }
    >
      {task.isPending && <LoadingBlock />}
      {task.isError && (
        <ErrorBlock
          message={task.error instanceof Error ? task.error.message : 'Failed to load task'}
        />
      )}
      {task.data && <TaskDetailBody taskId={taskId} task={task.data} />}
    </RecordShape>
  );
}

/**
 * Previous, the position, next. Absent without list context: there is no
 * list to walk. Disabled with an em dash when the list moved out from
 * under the task, so the operator sees why nothing steps.
 */
function NeighbourNav({
  neighbours,
  onStep,
}: {
  neighbours: TaskNeighbours;
  onStep: (to: Neighbour) => void;
}) {
  if (neighbours.status === 'absent') return null;
  const { status, position, total, prev, next } = neighbours;
  const label = status === 'ready' ? `${position} of ${total}` : status === 'moved' ? '—' : '…';
  return (
    <Inline align="center" gap={7} wrap={false}>
      <Button
        variant="secondary"
        size="sm"
        icon="chevron_left"
        aria-label="Previous task"
        disabled={!prev}
        onClick={() => prev && onStep(prev)}
      />
      <span
        title="position in list"
        style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}
        className="mono"
      >
        {label}
      </span>
      <Button
        variant="secondary"
        size="sm"
        icon="chevron_right"
        aria-label="Next task"
        disabled={!next}
        onClick={() => next && onStep(next)}
      />
    </Inline>
  );
}

function kickerFor(task: Task): string {
  if (task.status !== 'failed') return `Task · ${task.status}`;
  if (!task.started_at || !task.completed_at) return 'Task · failed';
  const ms = Math.max(0, Date.parse(task.completed_at) - Date.parse(task.started_at));
  return `Task · failed after ${ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`}`;
}
